import { ConditionalWrap } from "@/hoc/ConditionalWrap";
import {
  useProfile,
  useCreateProfile,
  useUpdateProfile,
  useUserData,
} from "@/hooks/queries";
import type { ProfileNewDataObject } from "@/hooks/queries";
import type { ProfileRes } from "@/types/profile";
import defaultImage from "@assets/default-profile-logo.png";
import { Breadcrumbs } from "@components/Breadcrumbs";
import Editor, { EditorStateChangeProps } from "@components/Editor/Editor";
import { DEFAULT_EDITOR_STATE } from "@components/Editor/constants";
import { ProfileDetailSkeleton } from "@components/Skeletons";
import NothingFound from "@components/NothingFound";
import PreContentForm from "@components/PreContentForm";
import { ProfileHeader } from "@components/ProfileHeader";
import { ProfileInfo } from "@components/ProfileIinfo";
import { ProfileImage } from "@components/ProfileImage";
import { ROUTER_PATHS } from "@constants/index";
import { yupResolver } from "@hookform/resolvers/yup";
import { formatDate, parseJson } from "@utils/index";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { v4 } from "uuid";
import * as yup from "yup";
import styles from "./styles.module.css";
import { useNotification } from "@/hooks/useNotification";

const schema = yup.object({
  name: yup
    .string()
    .trim()
    .required("Name is required")
    .test("Impossible characters", "Don’t use #, %, @, /", (val) => !val.match(/[%@#\/]/)),
});

export type FormParams = Partial<Omit<ProfileRes, "id" | "content">> & {
  name: string;
};

const defaultProfileFormValues: FormParams = {
  name: "",
  roles_ids: [],
  reps_ids: [],
  markets_ids: [],
  locations_ids: [],
  vendor: "",
  account_representative: "",
};

const EMPTY_EDITOR_STATE = {
  editor_state: JSON.stringify(DEFAULT_EDITOR_STATE),
  raw_page_content: "",
  page_content: "",
};

export const Profile = () => {
  const navigate = useNavigate();
  const { id = "" } = useParams();
  const { data: userData } = useUserData();
  const isUserData = !!userData;
  const isNewProfile = id === ROUTER_PATHS.newProfile;

  const { data: profileData, error: profileError } = useProfile(
    isNewProfile ? undefined : id
  );
  const createProfileMutation = useCreateProfile();
  const updateProfileMutation = useUpdateProfile();
  const { notifySuccess, notifyError } = useNotification();

  const [isEditing, setIsEditing] = useState(isNewProfile ? true : false);
  const [isSave, setIsSave] = useState(false);

  const profile: ProfileRes | FormParams | undefined = isNewProfile
    ? defaultProfileFormValues
    : profileData;

  const profileEditorState = {
    editor_state: JSON.stringify(
      (profile as ProfileRes)?.content ?? DEFAULT_EDITOR_STATE
    ),
    raw_page_content: "",
    page_content: "",
  };

  const [editorState, setEditorState] = useState<EditorStateChangeProps>(
    isNewProfile ? EMPTY_EDITOR_STATE : profileEditorState
  );

  const [editorKey, setEditorKey] = useState("editorKey");

  const oldProfileImage = profile?.image_url || defaultImage;

  const [profileImage, setProfileImage] = useState(oldProfileImage);

  const {
    control,
    handleSubmit,
    formState: { errors, touchedFields },
    setValue,
    setError,
    clearErrors,
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: (profile ?? defaultProfileFormValues) as FormParams,
  });

  const updateEditor = (state: EditorStateChangeProps) => {
    setEditorState(state);
    setEditorKey(`${v4()}`);
  };

  useEffect(() => {
    if (!isNewProfile && profileData?.content) {
      const content = JSON.stringify(profileData.content);
      updateEditor({
        editor_state: content,
        raw_page_content: "",
        page_content: "",
      });
    }
    if (!isNewProfile && profileData) {
      setProfileImage(profileData.image_url || defaultImage);
      Object.keys(profileData).forEach((key) => {
        if (["id", "content"].includes(key)) return;
        setValue(
          key as keyof FormParams,
          profileData[key as keyof ProfileRes] as FormParams[keyof FormParams]
        );
      });
    }
  }, [isNewProfile, profileData]);

  if (profileError) {
    return <NothingFound profileId={id} />;
  }

  const getDirtyFields = (data: FormParams) => {
    const dirtyFields: Partial<FormParams> = {};
    (Object.keys(data) as Array<keyof FormParams>).forEach((key) => {
      const dataValue = data[key];
      const profileValue = profile
        ? (profile as Record<string, unknown>)[key] ||
          defaultProfileFormValues[key]
        : defaultProfileFormValues[key];
      if (JSON.stringify(dataValue) !== JSON.stringify(profileValue)) {
        (dirtyFields as Record<string, unknown>)[key] = dataValue;
      }
    });
    return dirtyFields;
  };

  const prepareUpdatedData = (data: FormParams): ProfileNewDataObject => {
    const newData: Record<string, unknown> = {
      name: data.name as string,
    };
    const currentDate = new Date().toISOString();
    if (isNewProfile) {
      newData.created_at = currentDate;
      newData.updated_at = currentDate;
    } else {
      newData.updated_at = currentDate;
    }

    Object.keys(getDirtyFields(data)).forEach((key) => {
      newData[key] = data[key as keyof FormParams];
    });
    if (editorState.raw_page_content) {
      newData.raw_page_content = editorState.raw_page_content;
    }
    const parsedContent = parseJson(editorState.page_content);

    const imageChanged = oldProfileImage !== profileImage;
    const imageRemoved = imageChanged && !profileImage;
    const imageDataUrl = imageChanged && profileImage ? profileImage : undefined;

    return {
      data: newData as ProfileNewDataObject["data"],
      content: parsedContent,
      imageDataUrl: imageDataUrl || null,
      imageRemoved,
    };
  };

  const onSubmit = async (data: FormParams) => {
    setIsSave(true);
    const newDataObj = prepareUpdatedData(data);
    if (isNewProfile) {
      try {
        const res = await createProfileMutation.mutateAsync(newDataObj);
        if (res?.profile_id) {
          navigate(`/${ROUTER_PATHS.profile}/${res.profile_id}`);
          setIsEditing(false);
          notifySuccess("Profile created");
        }
      } catch {
        setError("name", { message: "Name is already exist" });
        notifyError("Failed to create profile");
      }
      setIsSave(false);
      return;
    } else {
      try {
        await updateProfileMutation.mutateAsync({
          id,
          ...newDataObj,
        });
        notifySuccess("Profile updated");
      } catch (e: unknown) {
        if (typeof e === "object" && e !== null && "message" in e) {
          const msg = (e as { message: string }).message;
          if (msg.includes("409")) {
            setIsSave(false);
            setError("name", { message: "Name is already exist" });
            return;
          }
        }
      }
    }
    setIsSave(false);
    setIsEditing(false);
  };

  const onReset = () => {
    updateEditor(profileEditorState);
    setIsEditing(false);
    setProfileImage(oldProfileImage);
    if (isNewProfile) {
      navigate(`${ROUTER_PATHS.main}`);
    } else {
      clearErrors();
      Object.keys(touchedFields).forEach((key) => {
        setValue(key as keyof FormParams, profile![key as keyof FormParams]);
      });
    }
  };

  const time = profile
    ? formatDate(profile.updated_at ?? profile.created_at)
    : "";

  return (
    <div className={styles.profileContainer}>
      {(!profileData && !isNewProfile) || isSave ? (
        <ProfileDetailSkeleton />
      ) : (
        <>
          {isUserData && <Breadcrumbs width="1040px" pageName="Profile" />}
      {profile && isUserData && (
        <div
          className={`${styles.profileWrapper} ${
            isSave ? styles.pointerEventNone : ""
          }`}
        >
          <ConditionalWrap
            condition={isEditing}
            wrap={(items: any) => (
              <form
                className="w100"
                onSubmit={handleSubmit(onSubmit)}
                onReset={onReset}
              >
                {items}
              </form>
            )}
          >
            <ProfileHeader
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              time={time}
            />
            <div className={styles.profileInfo}>
              <ProfileImage
                url={profileImage || defaultImage}
                alt={profile.name}
                isProfileEditing={isEditing}
                onPhotoUpdate={(img: string) => setProfileImage(img)}
                onCancelUpdate={() => setProfileImage(oldProfileImage)}
                onPhotoRemove={() => setProfileImage("")}
              />
              {isEditing ? (
                <PreContentForm
                  control={control}
                  errors={errors}
                  profileId={isNewProfile ? undefined : id}
                  relationshipLevel={profile.relationship_level}
                  top30={profile.top30}
                />
              ) : (
                <ProfileInfo profile={profile} profileId={isNewProfile ? undefined : id} />
              )}
            </div>
            <div className={isEditing ? styles.editorWrapper : undefined}>
              <Editor
                editable={isEditing}
                editorState={editorState.editor_state}
                editorKey={editorKey}
                onStateChange={(obj: EditorStateChangeProps) =>
                  setEditorState(obj)
                }
              />
            </div>
          </ConditionalWrap>
        </div>
      )}
        </>
      )}
    </div>
  );
};
